/**
 * HA-591 — what the till shows and allows for a dish that is 86'd.
 *
 * Pure, and separate from the tiles, because the interesting part is a precedence and a permission
 * matrix and both are cheap to enumerate here and expensive to reach by tapping.
 */

/** Mirrors the server's EightySixReason. */
export type EightySixReason = "None" | "Auto" | "Manual";

/** One ingredient standing between a dish and being sellable. */
export interface EightySixBlocker {
	stockItemId: string;
	/**
	 * No level document at this venue at all, as opposed to one that reached zero. A freshly
	 * provisioned venue shows its whole tracked menu as 86'd — the truthful state — and this is how
	 * the till says "setup is not finished" rather than "the kitchen has run out".
	 */
	neverStocked: boolean;
}

export interface EightySixStatus {
	productId: string;
	variantId: string | null;
	reason: EightySixReason;
	autoBlockedBy: EightySixBlocker[];
	manualNote: string | null;
	overrideExpiresAt: string | null;
	overrideActive: boolean;
}

/** The key the server indexes statuses by, and the one the tile has in hand. */
export function statusKey(
	productId: string,
	variantId?: string | null,
): string {
	return variantId ? `${productId}:${variantId}` : productId;
}

export function statusFor(
	statuses: Record<string, EightySixStatus>,
	productId: string,
	variantId?: string | null,
): EightySixStatus | undefined {
	return statuses[statusKey(productId, variantId)];
}

/**
 * What a write returns: the STORED half only. The status the tile renders is derived per read and
 * comes back from the status call, so a write is followed by an invalidate rather than by patching
 * a status together from this.
 */
export interface EightySixState {
	id: string;
	venueId: string;
	productId: string;
	variantId: string | null;
	manualSet: boolean;
	manualNote: string | null;
	overrideExpiresAt: string | null;
}

/** The dish the till is asking about. `variantId` null means the product itself. */
export interface EightySixSubject {
	productId: string;
	variantId: string | null;
}

/**
 * The subjects to ask the server about: exactly the tiles the grid is about to draw.
 *
 * <para>Derived from the same two inputs, and by the same rule, that `ProductSelector` uses to
 * decide which variants become buttons — `All` means every variant, otherwise only the ones the
 * menu item names. Asking about anything else is the bug worth guarding: ask about less and a tile
 * renders with no pill and sells something that is off; ask about the whole catalogue and the
 * server derives hundreds of answers to render forty.</para>
 *
 * <para>Typed structurally rather than against the catalogue's types, so this stays pure and
 * testable without dragging the product-catalogue package into a module about stock.</para>
 */
export function subjectsFor(
	menuItems: {
		productId?: string | null;
		variantSelectionMode?: string | null;
		productVariantIds?: string[] | null;
	}[],
	products: { id: string; variants?: { id: string }[] | null }[],
): EightySixSubject[] {
	const subjects: EightySixSubject[] = [];
	const seen = new Set<string>();

	for (const menuItem of menuItems) {
		const product = products.find((p) => p.id === menuItem.productId);
		if (!product) continue;

		const variants =
			(menuItem.variantSelectionMode === "All"
				? product.variants
				: product.variants?.filter((v) =>
						menuItem.productVariantIds?.includes(v.id),
					)) || [];

		for (const variant of variants) {
			const key = statusKey(product.id, variant.id);
			if (seen.has(key)) continue;

			seen.add(key);
			subjects.push({ productId: product.id, variantId: variant.id });
		}
	}

	return subjects;
}

export type PillTone = "manual" | "auto" | "override";

export interface EightySixPill {
	label: string;
	tone: PillTone;
}

/** AC copy. "86" is what the floor calls it and what the button says. */
export const MANUAL_LABEL = "86'd";
export const AUTO_LABEL = "Out of stock";
export const NEVER_STOCKED_LABEL = "Not stocked";
export const OVERRIDE_LABEL = "Override";

/**
 * The pill, or null for a sellable dish.
 *
 * <b>Precedence: Manual, then override, then Auto.</b> Manual is shown FIRST because it is the one
 * a person put there and the one nothing can sell through: an override applies only to an auto-86,
 * so a Manual pill and a working tile is a state that cannot arise. Under it the override outranks
 * the Auto it is suppressing, because it is what changes the answer to a tap.
 */
export function pillFor(
	status: EightySixStatus | undefined,
): EightySixPill | null {
	if (!status || status.reason === "None") return null;

	if (status.reason === "Manual")
		return { label: MANUAL_LABEL, tone: "manual" };

	if (status.overrideActive) return { label: OVERRIDE_LABEL, tone: "override" };

	// "Out of stock" and "not stocked" are different sentences and the second is not a fault. On a
	// new venue every tracked dish is blocked until the first delivery, and a till that says the
	// kitchen has run out on day one reads as a bug rather than as setup not being finished.
	const everStocked = status.autoBlockedBy.some((b) => !b.neverStocked);

	return {
		label: everStocked ? AUTO_LABEL : NEVER_STOCKED_LABEL,
		tone: "auto",
	};
}

/**
 * Whether tapping the tile adds the dish.
 *
 * <b>Manual blocks, and an override does not lift it.</b> A manual 86 is a person saying "we are
 * not selling this", and the way to undo that is to CLEAR it — a floor action anyone with
 * `stock:86:set` can take — not to sell through it. An override that defeated a chef's deliberate
 * pull would make the pull advisory, which is the opposite of what it is for.
 *
 * <b>Auto does not block.</b> An auto 86 is the level's opinion, which is frequently behind the
 * shelf — a half-case nobody logged, a delivery in the van — so the line is allowed on and the SEND
 * is where stock has the final say, which is where it already was before this feature.
 */
export function canAddToOrder(status: EightySixStatus | undefined): boolean {
	if (!status) return true;

	return status.reason !== "Manual";
}

/** Shown when a tap is refused, so the refusal explains itself rather than the tile just not working. */
export function blockedMessage(
	status: EightySixStatus | undefined,
): string | null {
	if (canAddToOrder(status) || !status) return null;

	// Says CLEAR, not override: an override applies only to an auto-86, so telling the floor a
	// supervisor can override this one sends them to a button that does not exist.
	return status.manualNote
		? `86'd — ${status.manualNote}`
		: "86'd. It can be cleared when the item is back.";
}

export interface EightySixPermissions {
	/** stock:86:set — floor staff hold this. */
	canSet: boolean;
	/** stock:86:override — a supervisor act. */
	canOverride: boolean;
}

export type EightySixAction = "set" | "clear" | "override";

/**
 * What the long-press menu offers for this dish, in the order it shows them.
 *
 * <b>Only actions that would change something.</b> Offering "86 it" on something already 86'd, or
 * "override" on something sellable, is a button whose only outcome is a no-op — and an operator who
 * taps one and sees nothing happen assumes the till is broken rather than that the action was
 * meaningless.
 */
export function actionsFor(
	status: EightySixStatus | undefined,
	permissions: EightySixPermissions,
): EightySixAction[] {
	const actions: EightySixAction[] = [];
	const manual = status?.reason === "Manual";

	if (permissions.canSet) actions.push(manual ? "clear" : "set");

	// An override is offered ONLY against an auto-86, and only when one is not already running.
	// Not against a Manual: the answer to "someone 86'd this and I disagree" is to clear it, which
	// is a floor action, not to sell through it. Offering override there would put a supervisor
	// button next to a decision a supervisor is not meant to overrule silently. And extending a
	// live override is not a thing the button does, so it is not offered either.
	if (
		permissions.canOverride &&
		status?.reason === "Auto" &&
		!status.overrideActive
	) {
		actions.push("override");
	}

	return actions;
}

/**
 * The path segment each write posts to.
 *
 * Set is the bare `86` while the other two are sub-paths, which is asymmetric enough to get wrong
 * silently: a clear posted to `86` would 86 the dish it was meant to un-86. Pinned by a test rather
 * than left as a ternary inside the fetch.
 */
export function writePath(action: EightySixAction): string {
	return action === "set" ? "86" : `86/${action}`;
}

export const ACTION_LABELS: Record<EightySixAction, string> = {
	set: "86 this item",
	clear: "Clear 86",
	override: "Override for this service",
};

/**
 * What an override is worth saying after it is granted: it runs out, and when.
 *
 * Formatted from the server's instant rather than recomputed, because the expiry was fixed when the
 * override was applied and a later change to the venue's rollover must not appear to move it.
 */
export function overrideExpiryLabel(
	status: EightySixStatus | undefined,
	formatTime: (iso: string) => string,
): string | null {
	if (!status?.overrideActive || !status.overrideExpiresAt) return null;

	return `Override until ${formatTime(status.overrideExpiresAt)}`;
}
